import React, { useState, useRef } from "react"
import { GatsbyImage } from "gatsby-plugin-image"
import { validatePostcode } from "@components/helpers/PostcodeCheck"
import { Link, navigate } from "gatsby"
import Trustpilot from "@components/Trustpilot"
import "@styles/components/slices/hero-postcode-section.scss"
import { useForegroundParallax } from "../../hooks/useForegroundParallax.hook"
import { RichText } from "prismic-reactjs"

const HeroPostcodeCheck = ({ slice }) => {
  const [postcodeInput, setPostcodeInput] = useState("")
  const [showNoDeliveryMessage, setShowNoDeliveryMessage] = useState(false)
  const [showSignupButton, setShowSignupButton] = useState(false)
  const [postcodeDeliveryData, setPostcodeDeliveryData] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  const hero = slice.primary

  const handlePostcodeChange = e => {
    e.preventDefault()
    setPostcodeInput(e.target.value)
  }

  const handlePostcodeCheck = async e => {
    e.preventDefault()
    if (!postcodeInput) {
      return
    }

    setIsLoading(true)

    const data = await validatePostcode(postcodeInput)

    if (data) {
      setPostcodeDeliveryData(data)
      setShowSignupButton(true)
      setIsLoading(false)
    } else {
      setShowNoDeliveryMessage(true)
      setIsLoading(false)
    }
  }

  const resetForm = () => {
    setPostcodeInput("")
    setShowNoDeliveryMessage(false)
    setPostcodeDeliveryData({})
  }

  const handleSubmit = e => {
    e.preventDefault()

    const registrationUrl = postcodeDeliveryData.deliveryConfigurationId
      ? encodeURI(
          `${process.env.GATSBY_SHOP_URL}?postcode=${postcodeDeliveryData.postcode}`,
        )
      : encodeURI(`${process.env.GATSBY_SHOP_URL}`)

    navigate(registrationUrl)
  }

  // Apply foreground parallax effect to image
  const imageRef = useRef(null)
  useForegroundParallax(imageRef, { speed: 0.07, maxOffset: 30 })

  return (
    <section className="hero-postcode">
      <div className="hero-postcode__wrapper">
        <div className="hero-postcode__intro">
          <div>
            <h1 className="hero-postcode__intro__title">{hero.title}</h1>
            <div className="hero-postcode__intro__description">
              <RichText render={hero.description.richText} />
            </div>
          </div>

          {isLoading && <div className="loader" />}
          {!showNoDeliveryMessage && !showSignupButton && !isLoading && (
            <div className="hero-postcode__postcode-check">
              <div className="hero-postcode__postcode-check-form-group">
                <form onSubmit={handlePostcodeCheck}>
                  <input
                    className="hero-postcode__postcode-check-input"
                    type="text"
                    placeholder="Check your postcode..."
                    value={postcodeInput}
                    onChange={handlePostcodeChange}
                  />
                  <input
                    type="submit"
                    value={hero.cta_text}
                    className="button button-primary-dark"
                  ></input>
                </form>
              </div>
            </div>
          )}

          {showSignupButton && (
            <div className="hero-postcode__postcode-check-info">
              <p className="hero-postcode__postcode-check-info__title">
                Good news! We deliver to your area overnight every{" "}
                <span className="hero-postcode__postcode-check-info__delivery-day">
                  {postcodeDeliveryData.day}
                </span>
              </p>
              <button
                onClick={handleSubmit}
                className="button button-primary"
                type="submit"
                value="Pick your box"
              >
                Pick your box
              </button>
            </div>
          )}

          {showNoDeliveryMessage && (
            <div className="hero-postcode__postcode-check-info">
              <p className="hero-postcode__postcode-check-info__title">
                Sorry, we don't seem to deliver to your postcode:{" "}
                <span className="hero-postcode__postcode-check-info__delivery-postcode">
                  {postcodeInput}
                </span>
              </p>
              <p className="hero-postcode__postcode-check-info__description">
                If you'd like to be notified as soon as we deliver to your area,
                please join our waiting list below.
              </p>
              <Link className="button button-secondary" to="/postcode-waitlist">
                Join our waiting list
              </Link>
              <button className="button button-primary" onClick={resetForm}>
                Try another postcode
              </button>
            </div>
          )}

          <Trustpilot fontFamily="Roboto" template="Horizontal" theme="dark" />
        </div>
        <div className="hero-postcode__image" ref={imageRef}>
          <GatsbyImage
            image={hero.hero_image.gatsbyImageData}
            alt={hero.hero_image.alt}
          />
        </div>
      </div>
    </section>
  )
}

export default HeroPostcodeCheck
